import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { KgEvaluationStatus } from '@/features/documents/utils';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { api } from '@/api';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { BidComplianceStatus } from '@/features/bid-compliance/constants';
import { useDocumentsIndexingStatus } from '@/features/documents/queries';
import { useProjectView } from '@/features/projects/helpers';
import { REFETCH_SUMMARIZATION_INTERVAL } from '@/features/workspaces/constants';
import { isEmptySnapshotRow, queryStream } from '@/features/workspaces/stream';
import type {
  BidAnalysisState,
  BidComplianceCoverageGroup,
  BidComplianceCoverageSection,
  GetTenderRequirementsResponse,
} from '@/features/bid-compliance/types';
import type { BidDeliverableCatalogItem } from '@/features/bid-deliverables/types';
import { bidAnalysisAlert } from '@/features/bid-compliance/utils';
import { bidCvDetailsQuery } from '@/features/bid-cvs/queries';
import {
  BID_METHODOLOGY_CATEGORIES_QUERY_KEY,
  bidMethodologyVersionsQuery,
} from '@/features/bid-methodology/queries';
import type { TenderCriterion, TenderCriterionRequirement } from '@/features/bids/types';

const BID_COMPLIANCE_QUERY_KEY = 'bidCompliance';

export const bidComplianceQuery = (workspaceId: string, bidId: string) =>
  queryOptions({
    queryKey: [BID_COMPLIANCE_QUERY_KEY, bidId],
    queryFn: async ({ client, queryKey }) => {
      const data = (
        await api.get<{
          coverageRunId: string | null;
          status: number | null;
          progress: number;
          rows: BidComplianceCoverageGroup[];
          sections: BidComplianceCoverageSection[];
          deliverableCatalog?: {
            state: 'not_initialized' | 'waiting_for_inputs' | 'building' | 'ready' | 'failed';
            tenderCriteria: TenderCriterion[];
            requirements?: TenderCriterionRequirement[];
            items: BidDeliverableCatalogItem[];
          } | null;
          analysisState?: BidAnalysisState | null;
        }>('v1/analysis/bid_submission_coverage', {
          params: { type: DOCUMENT_TYPE.tenderPackage, resourceId: workspaceId, bidId },
        })
      ).data;
      const previous = client.getQueryData<{ analysisState?: BidAnalysisState | null }>(queryKey);
      if (
        previous?.analysisState?.refresh.scoringPending &&
        !data.analysisState?.refresh.scoringPending
      )
        await Promise.all([
          client.invalidateQueries(bidCvDetailsQuery(bidId)),
          client.invalidateQueries(bidMethodologyVersionsQuery(bidId)),
          client.invalidateQueries({ queryKey: [BID_METHODOLOGY_CATEGORIES_QUERY_KEY, bidId] }),
        ]);
      return data;
    },
    refetchInterval: (query) =>
      (query.state.data?.status === BidComplianceStatus.DONE ||
        query.state.data?.status === BidComplianceStatus.FAILED) &&
      !['checking', 'processing'].includes(
        bidAnalysisAlert(query.state.data.analysisState) ?? '',
      ) &&
      !query.state.data.analysisState?.refresh.scoringPending &&
      !['not_initialized', 'waiting_for_inputs', 'building'].includes(
        query.state.data.deliverableCatalog?.state ?? '',
      )
        ? false
        : REFETCH_SUMMARIZATION_INTERVAL * 1000,
  });

const TENDER_REQUIREMENTS_TERMINAL_STATUSES: readonly BidComplianceStatus[] = [
  BidComplianceStatus.DONE,
  BidComplianceStatus.FAILED,
];

export const tenderRequirementsQuery = (resourceId: string) => {
  const queryKey = ['tenderRequirements', resourceId] as const;
  return queryOptions({
    queryKey,
    queryFn: async () =>
      (
        await api.get<GetTenderRequirementsResponse>('v1/analysis/tender_requirements', {
          params: { tenderId: resourceId, type: DOCUMENT_TYPE.tenderPackage, evalId: resourceId },
        })
      ).data,
    meta: {
      stream: queryStream<
        GetTenderRequirementsResponse,
        {
          id?: string | null;
          status: BidComplianceStatus | null;
          progress: number | null;
        }
      >({
        topic: 'tender_submission_requirements',
        type: DOCUMENT_TYPE.tenderPackage,
        resourceId,
        isActive: (data) =>
          !!data &&
          (data.status == null ||
            data.status === BidComplianceStatus.NOT_STARTED ||
            data.status === BidComplianceStatus.IN_PROGRESS),
        onStatusUpdate: (row, queryClient) => {
          if (isEmptySnapshotRow(row)) return queryClient.invalidateQueries({ queryKey });
          if (row.status == null) return;

          const status = row.status;
          const progress = Number(row.progress);
          queryClient.setQueryData<GetTenderRequirementsResponse>(
            queryKey,
            (old) => old && { ...old, status, progress: Number.isFinite(progress) ? progress : 0 },
          );
          if (TENDER_REQUIREMENTS_TERMINAL_STATUSES.includes(status))
            return Promise.all([
              queryClient.invalidateQueries({ queryKey }),
              queryClient.invalidateQueries({ queryKey: [BID_COMPLIANCE_QUERY_KEY] }),
            ]);
        },
      }),
    },
  });
};

export function useBidCompliance() {
  const { workspaceId, bidId } = useParams({
    from: '/_authed/project/$projectId/workspaces/$workspaceId/bids/$bidId/_layout',
  });
  const view = useProjectView();
  const { data, isLoading } = useQuery(bidComplianceQuery(workspaceId, bidId));

  const bid = useDocumentsIndexingStatus({ documentType: 'bid', id: bidId });
  const tender = useDocumentsIndexingStatus({
    documentType: 'tenderPackage',
    id: workspaceId,
  });
  const bidIndexingLabel = bid.areDocumentsIndexed
    ? null
    : bid.isKgBuilding
      ? t(common.waitForKeyInformation)
      : t(common.waitForBidDocumentsIndexing);
  const needsExtraction =
    data?.analysisState?.refresh.errorCode === 'extraction_failed' ||
    tender.data?.kgStatus !== KgEvaluationStatus.Done ||
    bid.data?.kgStatus !== KgEvaluationStatus.Done;
  const statusKnown = (resource: typeof bid) =>
    resource.hasNoDocuments || resource.data !== undefined || resource.isError;

  return {
    data,
    isLoading: isLoading || !statusKnown(tender) || !statusKnown(bid),
    status: data?.status ?? BidComplianceStatus.NOT_STARTED,
    tenderHasNoDocuments: tender.hasNoDocuments,
    bidHasNoDocuments: bid.hasNoDocuments,
    bidIndexingLabel,
    needsExtraction,
    isExtracting:
      tender.data?.kgStatus === KgEvaluationStatus.InProgress ||
      bid.data?.kgStatus === KgEvaluationStatus.InProgress,
    runBlockedReason: tender.hasNoDocuments
      ? t({
          en: 'To run the compliance checklist, upload tender package documents first.',
          nb: 'For å kjøre kravsjekken, last opp konkurransegrunnlagsdokumenter først.',
        })
      : bid.hasNoDocuments
        ? t({
            en:
              view === 'client'
                ? 'To run the compliance checklist, upload submission documents first.'
                : 'To run the compliance checklist, upload bid documents first.',
            nb:
              view === 'client'
                ? 'For å kjøre kravsjekken, last opp innleveringsdokumenter først.'
                : 'For å kjøre kravsjekken, last opp tilbudsdokumenter først.',
          })
        : null,
    indexingLabel: !tender.areDocumentsIndexed
      ? tender.isKgBuilding
        ? t(common.waitForKeyInformation)
        : t(common.waitForTenderPackageDocumentsIndexing)
      : (data?.status ?? BidComplianceStatus.NOT_STARTED) === BidComplianceStatus.NOT_STARTED
        ? bidIndexingLabel
        : null,
  };
}
